import { useState, useEffect } from 'react';
import {
  List, Accordion, AccordionSummary, AccordionDetails, ListItem, ListItemText, Typography,
  Chip, Divider, Box, Stack, Grid, Button, Avatar, ListItemAvatar, Modal, IconButton,
  CircularProgress, Paper, TextField, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fetchClients, fetchMails, saveClient, updateMail, uploadCheckImage, deleteCheckImage } from '../api/github';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import MailIcon from '@mui/icons-material/Mail';
import SearchIcon from '@mui/icons-material/Search';
import LoadingAnimation from './LoadingAnimation';
import uz from 'date-fns/locale/uz';
import AddMailModal from './AddMailModal';
import FloatingActionButton from './FloatingActionButton';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const formatCurrency = (amount) => {
  return amount ? `${amount.toLocaleString()} Сум` : '0 Сум';
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Йозилмаган';
  const date = new Date(dateString);
  return date.toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AddPaymentModal = ({ open, onClose, item, clientId, onAddPayment, isClientOrder }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalProductsAmount = item.products?.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0) || 0;

  const handleSubmit = async () => {
    if (!amount || isNaN(amount) || amount <= 0) {
      alert('Введите корректную сумму платежа');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddPayment(isClientOrder ? clientId : item.id, isClientOrder ? item.id : null, {
        amount: parseFloat(amount),
        date,
        comment
      }, isClientOrder);
      onClose(true);
    } catch (error) {
      console.error('Ошибка при добавлении платежа:', error);
      alert(error.message || 'Не удалось добавить платеж');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !isSubmitting && onClose(false)}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        width: '90%',
        maxWidth: 500,
        p: 3,
      }}>
        <Typography variant="h6" gutterBottom>
          Тўлов қўшиш
        </Typography>
        <Typography variant="body1" gutterBottom>
          Махсулот суммаси: {formatCurrency(totalProductsAmount)}
        </Typography>
        <Typography variant="body1" gutterBottom>
          Тўланган сумма: {formatCurrency(isClientOrder ? item.paymentInfo?.paidAmount || 0 : item.paidAmount || 0)}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <TextField
          label="Тўлов суммаси"
          type="number"
          fullWidth
          margin="normal"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{ inputProps: { min: 0 } }}
          required
          disabled={isSubmitting}
        />
        <TextField
          label="Тўлов санаси"
          type="datetime-local"
          fullWidth
          margin="normal"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isSubmitting}
        />
        <TextField
          label="Комментарий"
          fullWidth
          margin="normal"
          size="small"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={isSubmitting}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={<PaymentIcon />}
          >
            {isSubmitting ? 'Сақланияпти...' : 'Сақлаш'}
          </Button>
          <Button onClick={() => onClose(false)} disabled={isSubmitting}>
            Бекор қилиш
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default function MailList({ isMobile, refreshTrigger = null }) {
  const [clients, setClients] = useState([]);
  const [mails, setMails] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isClientOrder, setIsClientOrder] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('date');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedItemForCheck, setSelectedItemForCheck] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checkToDelete, setCheckToDelete] = useState(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [clientsData, mailsData] = await Promise.all([
          fetchClients(),
          fetchMails()
        ]);
        const formattedClients = clientsData.map(client => ({
          ...client,
          type: 'client',
          orders: client.orders?.map(order => ({
            ...order,
            checkImages: order.checkImages?.map(check =>
              typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
            ) || []
          })) || []
        }));
        const formattedMails = mailsData.map(mail => ({
          ...mail,
          type: 'mail',
          checkImages: mail.checkImages?.map(check =>
            typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
          ) || []
        }));
        setClients(formattedClients || []);
        setMails(formattedMails || []);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        alert(error.message || 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [refreshTrigger]);

  const handleCheckUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const currentItem = selectedItemForCheck;
    const currentClientId = selectedClientId;

    if (!currentItem) {
      alert('Выберите заказ или почту');
      return;
    }

    try {
      setIsSubmitting(true);
      const uploadedUrls = await Promise.all(
        files.map(file => {
          if (file.size > MAX_FILE_SIZE) {
            throw new Error(`Файл ${file.name} слишком большой. Максимальный размер: 5MB`);
          }
          return uploadCheckImage(file);
        })
      );

      const newChecks = uploadedUrls.map(url => ({
        id: `check_${Date.now()}_${Math.random()}`,
        url,
        isPaid: true
      }));

      if (currentItem.type === 'client') {
        const updatedClients = [...clients];
        const clientIndex = updatedClients.findIndex(c => c.id === currentClientId);
        const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === currentItem.id);
        const existingChecks = Array.isArray(updatedClients[clientIndex].orders[orderIndex].checkImages)
          ? updatedClients[clientIndex].orders[orderIndex].checkImages
          : [];
        updatedClients[clientIndex].orders[orderIndex].checkImages = [...existingChecks, ...newChecks];
        await saveClient(updatedClients[clientIndex]);
        setClients(updatedClients);
      } else {
        const updatedMails = [...mails];
        const mailIndex = updatedMails.findIndex(m => m.id === currentItem.id);
        const existingChecks = Array.isArray(updatedMails[mailIndex].checkImages)
          ? updatedMails[mailIndex].checkImages
          : [];
        updatedMails[mailIndex].checkImages = [...existingChecks, ...newChecks];
        await updateMail(updatedMails[mailIndex].id, updatedMails[mailIndex]);
        setMails(updatedMails);
      }
    } catch (error) {
      console.error("Ошибка загрузки чеков:", error);
      alert(error.message || 'Ошибка загрузки чеков');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCheck = async (checkId, item) => {
    try {
      setIsSubmitting(true);

      if (item.type === 'client') {
        const updatedClients = [...clients];
        const clientIndex = updatedClients.findIndex(c => c.id === selectedClientId);
        const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === item.id);
        const check = updatedClients[clientIndex].orders[orderIndex].checkImages.find(c => c.id === checkId);
        if (check) {
          await deleteCheckImage(check.url);
          updatedClients[clientIndex].orders[orderIndex].checkImages =
            updatedClients[clientIndex].orders[orderIndex].checkImages.filter(c => c.id !== checkId);
          await saveClient(updatedClients[clientIndex]);
          setClients(updatedClients);
        }
      } else {
        const updatedMails = [...mails];
        const mailIndex = updatedMails.findIndex(m => m.id === item.id);
        const check = updatedMails[mailIndex].checkImages.find(c => c.id === checkId);
        if (check) {
          await deleteCheckImage(check.url);
          updatedMails[mailIndex].checkImages =
            updatedMails[mailIndex].checkImages.filter(c => c.id !== checkId);
          await updateMail(updatedMails[mailIndex].id, updatedMails[mailIndex]);
          setMails(updatedMails);
        }
      }
    } catch (error) {
      console.error("Ошибка при удалении чека:", error);
      alert(error.message || 'Ошибка при удалении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteCheck = async () => {
    if (!checkToDelete || !selectedItemForCheck) return;

    try {
      setIsSubmitting(true);

      if (selectedItemForCheck.type === 'client') {
        const updatedClients = [...clients];
        const clientIndex = updatedClients.findIndex(c => c.id === selectedClientId);
        const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === selectedItemForCheck.id);
        const check = updatedClients[clientIndex].orders[orderIndex].checkImages.find(c => c.id === checkToDelete);
        if (check) {
          await deleteCheckImage(check.url);
          updatedClients[clientIndex].orders[orderIndex].checkImages =
            updatedClients[clientIndex].orders[orderIndex].checkImages.filter(c => c.id !== checkToDelete);
          await saveClient(updatedClients[clientIndex]);
          setClients(updatedClients);
        }
      } else {
        const updatedMails = [...mails];
        const mailIndex = updatedMails.findIndex(m => m.id === selectedItemForCheck.id);
        const check = updatedMails[mailIndex].checkImages.find(c => c.id === checkToDelete);
        if (check) {
          await deleteCheckImage(check.url);
          updatedMails[mailIndex].checkImages =
            updatedMails[mailIndex].checkImages.filter(c => c.id !== checkToDelete);
          await updateMail(updatedMails[mailIndex].id, updatedMails[mailIndex]);
          setMails(updatedMails);
        }
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Ошибка при удалении чека:", error);
      alert(error.message || 'Ошибка при удалении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleCheckPayment = async (checkId, isCurrentlyPaid, item) => {
    try {
      setIsSubmitting(true);

      if (item.type === 'client') {
        const updatedClients = [...clients];
        const clientIndex = updatedClients.findIndex(c => c.id === selectedClientId);
        const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === item.id);
        updatedClients[clientIndex].orders[orderIndex].checkImages =
          updatedClients[clientIndex].orders[orderIndex].checkImages.map(check =>
            check.id === checkId
              ? {
                  ...check,
                  isPaid: true,
                  paidDate: !isCurrentlyPaid ? new Date().toISOString() : null
                }
              : check
          );
        await saveClient(updatedClients[clientIndex]);
        setClients(updatedClients);
      } else {
        const updatedMails = [...mails];
        const mailIndex = updatedMails.findIndex(m => m.id === item.id);
        updatedMails[mailIndex].checkImages =
          updatedMails[mailIndex].checkImages.map(check =>
            check.id === checkId
              ? {
                  ...check,
                  isPaid: true,
                  paidDate: !isCurrentlyPaid ? new Date().toISOString() : null
                }
              : check
          );
        await updateMail(updatedMails[mailIndex].id, updatedMails[mailIndex]);
        setMails(updatedMails);
      }
    } catch (error) {
      console.error("Ошибка при обновлении чека:", error);
      alert(error.message || 'Ошибка при обновлении чека');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPayment = async (id, orderId, paymentData, isClientOrder) => {
    try {
      if (isClientOrder) {
        const updatedClients = [...clients];
        const clientIndex = updatedClients.findIndex(c => c.id === id);
        if (clientIndex === -1) throw new Error('Клиент не найден');

        const orderIndex = updatedClients[clientIndex].orders.findIndex(o => o.id === orderId);
        if (orderIndex === -1) throw new Error('Заказ не найден');

        const order = updatedClients[clientIndex].orders[orderIndex];
        if (!order.paymentInfo.prepayments) {
          order.paymentInfo.prepayments = [];
        }

        order.paymentInfo.prepayments.push({
          amount: paymentData.amount,
          date: paymentData.date,
          comment: paymentData.comment || ''
        });

        order.paymentInfo.paidAmount = order.paymentInfo.prepayments.reduce(
          (sum, payment) => sum + parseFloat(payment.amount || 0),
          0
        );

        if (order.paymentInfo.paidAmount >= order.totalAmount) {
          order.status = 'Тўллиқ тўланган';
        } else if (order.paymentInfo.paidAmount > 0) {
          order.status = 'Охиргача тўланмаган';
        }

        await saveClient(updatedClients[clientIndex]);
        setClients(updatedClients);
      } else {
        const updatedMails = [...mails];
        const mailIndex = updatedMails.findIndex(m => m.id === id);
        if (mailIndex === -1) throw new Error('Почта не найдена');

        const mail = updatedMails[mailIndex];
        const updatedPayments = [...(mail.payments || []), {
          amount: paymentData.amount,
          date: paymentData.date,
          comment: paymentData.comment || ''
        }];
        const totalProductsAmount = mail.products?.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0) || 0;
        const paidAmount = updatedPayments.reduce((sum, p) => sum + p.amount, 0);

        let status = mail.status;
        if (paidAmount >= totalProductsAmount) {
          status = 'completed';
        } else if (paidAmount > 0) {
          status = 'processed';
        }

        updatedMails[mailIndex] = {
          ...mail,
          payments: updatedPayments,
          paidAmount,
          status,
          amount: totalProductsAmount
        };

        await updateMail(id, updatedMails[mailIndex]);
        setMails(updatedMails);
      }
      return true;
    } catch (error) {
      console.error("Ошибка при сохранении платежа:", error);
      throw error;
    }
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const combinedItems = [
    ...clients.flatMap(client =>
      client.orders?.map(order => ({
        type: 'client',
        id: order.id,
        clientId: client.id,
        nickname: client.nickname,
        employeeName: order.employeeName,
        date: order.deliveryInfo?.scheduledTime || order.date,
        paidAmount: order.paymentInfo?.paidAmount || 0,
        totalAmount: order.totalAmount || 0,
        status: order.status || 'Тўланмаган',
        products: order.products || [],
        checkImages: order.checkImages || [],
        paymentInfo: order.paymentInfo || { prepayments: [], paidAmount: 0 },
        deliveryInfo: order.deliveryInfo || {},
        partyDate: order.partyDate,
        partyComment: order.partyComment,
        review: order.review,
        code: client.code
      })) || []
    ),
    ...mails.map(mail => ({
      type: 'mail',
      id: mail.id,
      clientId: null,
      nickname: mail.nickname,
      employeeName: mail.employeeName,
      date: mail.createdAt,
      paidAmount: mail.paidAmount || 0,
      totalAmount: mail.products?.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0) || 0,
      status: (mail.paidAmount || 0) >= (mail.products?.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0) || 0)
        ? 'completed'
        : (mail.paidAmount || 0) > 0
        ? 'processed'
        : 'new',
      products: mail.products || [],
      checkImages: mail.checkImages || [],
      payments: mail.payments || [],
      deliveryInfo: mail.deliveryInfo || {},
      partyDate: mail.partyDate,
      partyComment: mail.partyComment,
      code: mail.code,
      age: mail.age,
      address: mail.address,
      completedAt: mail.completedAt
    }))
  ];

  const filteredItems = combinedItems.filter(item => {
    const matchesSearch =
      filterField === 'nickname' && item.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'code' && item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'employee' && item.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filterField === 'date' && isSameDate(item.date, selectedDate);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'paid' && (item.status === 'Тўллиқ тўланган' || item.status === 'completed')) ||
      (statusFilter === 'unpaid' && (item.status !== 'Тўллиқ тўланган' && item.status !== 'completed'));

    return matchesSearch && matchesStatus;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uz}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FloatingActionButton
            onClick={() => setMailModalOpen(true)}
            tooltip="Почта қўшиш"
          />
        </Box>
        {loading ? (
          <LoadingAnimation loading={loading} />
        ) : combinedItems.length === 0 ? (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Мижозлар ва почталар мавжуд эмас
          </Typography>
        ) : (
          <List>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Фильтр по</InputLabel>
                <Select
                  value={filterField}
                  onChange={(e) => {
                    setFilterField(e.target.value);
                    if (e.target.value !== 'date') {
                      setSearchTerm('');
                    }
                  }}
                  label="Фильтр по"
                >
                  <MenuItem value="employee">Ходим</MenuItem>
                  <MenuItem value="nickname">Ник</MenuItem>
                  <MenuItem value="code">Код</MenuItem>
                  <MenuItem value="date">Сана</MenuItem>
                </Select>
              </FormControl>
              {filterField === 'date' ? (
                <DatePicker
                  label="Выберите дату"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                    },
                  }}
                />
              ) : (
                <TextField
                  placeholder="Қидириш..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  fullWidth
                />
              )}

            </Box>
            {filteredItems.map(item => (
              <Accordion key={`${item.type}-${item.id}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <ListItemText
                    primary={`Чиқарилган сана: ${formatDateTime(item.date)}`}
                    secondary={`Мижоз: ${item.nickname} | Ходим: ${item.employeeName || 'Йўқ'}`}
                  />
                  <Chip
                    label={formatCurrency(item.paidAmount)}
                    sx={{
                      ml: 2,
                      color: 'white',
                      background: item.status === 'Тўллиқ тўланган' || item.status === 'completed' ? 'green' : 'red'
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {item.type === 'client' ? `${item.employeeName} дан буюртма` : `${item.employeeName} томонидан почта`} {formatDateTime(item.date)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Код: {item.code || 'Йўқ'}
                    </Typography>
                    {item.products?.length > 0 && (
                      <>
                        <List dense>
                          {item.products.map((product, idx) => (
                            <ListItem key={idx}>
                              <ListItemAvatar>
                                <Avatar
                                  src={product.productImage}
                                  alt={product.productName}
                                  variant="square"
                                  sx={{ width: 56, height: 56, mr: 2 }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={`${product.productName || 'Махсулот'} x${product.quantity}`}
                                secondary={
                                  <Typography component="span" display="block">
                                    {formatCurrency(product.productPrice * product.quantity)}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                          Махсулотлар суммаси: {formatCurrency(item.totalAmount)}
                        </Typography>
                      </>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <ImageIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">{item.type === 'client' ? 'Чеклар' : 'Почта Расми'}</Typography>
                      </Stack>
                      {item.checkImages?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                          {item.checkImages.map((check) => {
                            const checkId = check.id;
                            const checkUrl = check.url;
                            const isPaid = check.isPaid;
                            const paidDate = check.paidDate;

                            return (
                              <Box
                                key={checkId}
                                sx={{
                                  position: 'relative',
                                  border: isPaid ? '2px solid green' : '2px solid transparent',
                                  borderRadius: 1
                                }}
                              >
                                <img
                                  src={checkUrl}
                                  alt={item.type === 'client' ? `Чек ${checkId}` : `Почта фото ${checkId}`}
                                  style={{
                                    width: 150,
                                    height: 150,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                />
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5
                                }}>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      color: isPaid ? 'success.main' : 'action.disabled'
                                    }}
                                    onClick={() => {
                                      setSelectedItemForCheck(item);
                                      setSelectedClientId(item.clientId);
                                      handleToggleCheckPayment(checkId, isPaid, item);
                                    }}
                                    disabled={isSubmitting}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)'
                                    }}
                                    onClick={() => window.open(checkUrl, '_blank')}
                                  >
                                    <ZoomInIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.7)',
                                      color: 'error.main'
                                    }}
                                    onClick={() => {
                                      setSelectedItemForCheck(item);
                                      setSelectedClientId(item.clientId);
                                      setCheckToDelete(checkId);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {isPaid && paidDate && (
                                  <Typography variant="caption" sx={{
                                    position: 'absolute',
                                    bottom: 4,
                                    left: 4,
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    px: 0.5,
                                    borderRadius: 1
                                  }}>
                                    {formatDateTime(paidDate)}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {item.type === 'client' ? 'Чеклар йўқ' : 'Почта расми йўқ'}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        disabled={isSubmitting}
                        onClick={() => {
                          setSelectedItemForCheck(item);
                          setSelectedClientId(item.clientId);
                          document.getElementById(`check-upload-input-${item.id}`).click();
                        }}
                        fullWidth
                      >
                        {item.type === 'client' ? 'Чек қўшиш' : 'Почта Фото қўшиш'}
                        <input
                          id={`check-upload-input-${item.id}`}
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={handleCheckUpload}
                        />
                      </Button>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <PaymentIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Тўлов маълумотлари</Typography>
                      </Stack>
                      <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Сумма</TableCell>
                              <TableCell>Сана</TableCell>
                              <TableCell>Изоҳ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(item.type === 'client' ? item.paymentInfo?.prepayments : item.payments)?.map((payment, index) => (
                              <TableRow key={index}>
                                <TableCell>{formatCurrency(payment.amount)}</TableCell>
                                <TableCell>{formatDateTime(payment.date)}</TableCell>
                                <TableCell>{payment.comment || 'Йозилмаган'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Grid container spacing={2}>
                        {(item.status !== 'Тўллиқ тўланган' && item.status !== 'completed') && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => {
                              setSelectedItem(item);
                              setSelectedClientId(item.clientId);
                              setIsClientOrder(item.type === 'client');
                              setPaymentModalOpen(true);
                            }}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            Тўлов қўшиш
                          </Button>
                        )}
                        <Grid>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Жами тўланган: {formatCurrency(item.paidAmount)}
                          </Typography>
                          {item.paidAmount > item.totalAmount && (
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'green' }}>
                              Ортиқча тўлов: {formatCurrency(item.paidAmount - item.totalAmount)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{
                            fontWeight: 'bold',
                            color: item.totalAmount - item.paidAmount > 0 ? 'red' : 'green'
                          }}>
                            {item.totalAmount - item.paidAmount > 0
                              ? "Тўланмаган сумма: " + formatCurrency(Math.max(0, item.totalAmount - item.paidAmount))
                              : ""}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <MailIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2">Холати</Typography>
                      </Stack>
                      <Typography variant="body2">
                        Ҳолати: {item.status === 'completed' ? 'Тўлиқ тўланган' : item.status === 'processed' ? 'Қисман тўланган' : item.status}
                      </Typography>
                      {item.completedAt && (
                        <Typography variant="body2">
                          Бажарилган сана: {formatDateTime(item.completedAt)}
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2">Той маълумотлари</Typography>
                      </Stack>
                      <Typography variant="body2" gutterBottom>
                        Той санаси: {formatDateTime(item.partyDate)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Той маълумотлари: {item.partyComment ? item.partyComment : 'Малумот йўқ'}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2">Етказиб бериш</Typography>
                      </Stack>
                      <Typography variant="body2" gutterBottom>
                        Манзил: {item.deliveryInfo?.address || 'Йозилмаган'}
                      </Typography>
                      <Typography variant="body2">
                        П. Чиқарилган: {formatDateTime(item.deliveryInfo?.scheduledTime)}
                      </Typography>
                      <Typography variant="body2">
                        П. Йетказилган: {formatDateTime(item.deliveryInfo?.actualTime)}
                      </Typography>
                    </Box>
                    {item.type === 'mail' && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" gap={1} mb={1}>
                            <Typography variant="subtitle2">Қўшимча маълумот</Typography>
                          </Stack>
                          <Typography variant="body2">
                            Йоши: {item.age || 'Йозилмаган'}
                          </Typography>
                          <Typography variant="body2">
                            Адрес: {item.address || 'Йозилмаган'}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {item.review && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Stack direction="row" alignItems="center" gap={1} mb={1}>
                            <Typography variant="subtitle2">Мижоз баҳоси</Typography>
                          </Stack>
                          <Typography variant="body2">
                            {item.review}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </List>
        )}
        <AddPaymentModal
          open={paymentModalOpen}
          onClose={(shouldRefresh) => {
            setPaymentModalOpen(false);
            if (shouldRefresh) {
              const loadData = async () => {
                setLoading(true);
                try {
                  const [clientsData, mailsData] = await Promise.all([
                    fetchClients(),
                    fetchMails()
                  ]);
                  const formattedClients = clientsData.map(client => ({
                    ...client,
                    type: 'client',
                    orders: client.orders?.map(order => ({
                      ...order,
                      checkImages: order.checkImages?.map(check =>
                        typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
                      ) || []
                    })) || []
                  }));
                  const formattedMails = mailsData.map(mail => ({
                    ...mail,
                    type: 'mail',
                    checkImages: mail.checkImages?.map(check =>
                      typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
                    ) || []
                  }));
                  setClients(formattedClients || []);
                  setMails(formattedMails || []);
                } catch (error) {
                  console.error('Ошибка загрузки данных:', error);
                  alert(error.message || 'Ошибка загрузки данных');
                } finally {
                  setLoading(false);
                }
              };
              loadData();
            }
          }}
          item={selectedItem || {
            totalAmount: 0,
            paymentInfo: { prepayments: [], paidAmount: 0 },
            paidAmount: 0,
            payments: []
          }}
          clientId={selectedClientId}
          onAddPayment={handleAddPayment}
          isClientOrder={isClientOrder}
        />
        <AddMailModal
          open={mailModalOpen}
          onClose={(shouldRefresh) => {
            setMailModalOpen(false);
            if (shouldRefresh) {
              const loadData = async () => {
                setLoading(true);
                try {
                  const [clientsData, mailsData] = await Promise.all([
                    fetchClients(),
                    fetchMails()
                  ]);
                  const formattedClients = clientsData.map(client => ({
                    ...client,
                    type: 'client',
                    orders: client.orders?.map(order => ({
                      ...order,
                      checkImages: order.checkImages?.map(check =>
                        typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
                      ) || []
                    })) || []
                  }));
                  const formattedMails = mailsData.map(mail => ({
                    ...mail,
                    type: 'mail',
                    checkImages: mail.checkImages?.map(check =>
                      typeof check === 'string' ? { id: `check_${Date.now()}_${Math.random()}`, url: check, isPaid: true } : check
                    ) || []
                  }));
                  setClients(formattedClients || []);
                  setMails(formattedMails || []);
                } catch (error) {
                  console.error('Ошибка загрузки данных:', error);
                  alert(error.message || 'Ошибка загрузки данных');
                } finally {
                  setLoading(false);
                }
              };
              loadData();
            }
          }}
        />
        <ConfirmDeleteModal
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDeleteCheck}
          itemName="Чек"
          title="Чекни ўчириш"
          message="Хақиқатан хам бу чекни ўчирмоқчимисиз?"
          confirmText="Ҳа"
          cancelText="Йўқ"
        />
      </Box>
    </LocalizationProvider>
  );
}


              // <FormControl sx={{ minWidth: 120 }} size="small">
              //   <InputLabel>Тўлов ҳолати</InputLabel>
              //   <Select
              //     value={statusFilter}
              //     onChange={(e) => setStatusFilter(e.target.value)}
              //     label="Тўлов ҳолати"
              //   >
              //     <MenuItem value="all">Ҳаммаси</MenuItem>
              //     <MenuItem value="paid">Тўланган</MenuItem>
              //     <MenuItem value="unpaid">Тўланмаган</MenuItem>
              //   </Select>
              // </FormControl>